import { useState } from "react";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  NumberInput,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconTrash, IconPencil, IconX, IconCheck } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { SafeDateInput } from "@/components/SafeDateInput";
import type {
  Order,
  Payment,
  PaymentDirection,
  PaymentSummary,
  PaymentsListResponse,
  PaymentCreateRequest,
} from "@/types/api";

// PaymentsTab — вкладка «Расчёты» карточки заявки.
// Показывает две ленты: От заказчика (incoming) и Перевозчику (outgoing),
// сводку по оплате/долгу для обеих сторон и текущую комиссию.
// Аналог вкладки «Расчёты» из Trans-Manager (см. project_payments_block.md).

interface Props {
  order: Order;
  isLocked: boolean;
}

function fmt(n: number): string {
  return n.toLocaleString("ru-RU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toDateISO(d: Date | null): string {
  if (!d) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export function PaymentsTab({ order, isLocked }: Props) {
  const qc = useQueryClient();
  const orderID = order.id;

  const paymentsQ = useQuery({
    queryKey: ["orders", orderID, "payments"],
    queryFn: async () =>
      (await api.get<PaymentsListResponse>(`/api/v1/orders/${orderID}/payments`)).data,
  });

  const summaryQ = useQuery({
    queryKey: ["orders", orderID, "payments-summary"],
    queryFn: async () =>
      (await api.get<PaymentSummary>(`/api/v1/orders/${orderID}/payments-summary`)).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["orders", orderID, "payments"] });
    qc.invalidateQueries({ queryKey: ["orders", orderID, "payments-summary"] });
  };

  const createMut = useMutation({
    mutationFn: async (body: PaymentCreateRequest) => {
      const r = await api.post<Payment>(`/api/v1/orders/${orderID}/payments`, body);
      return r.data;
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Платёж добавлен", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось добавить платёж", color: "red" }),
  });

  const updateMut = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: PaymentCreateRequest }) => {
      const r = await api.patch<Payment>(`/api/v1/orders/${orderID}/payments/${id}`, body);
      return r.data;
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Платёж обновлён", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось обновить платёж", color: "red" }),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/api/v1/orders/${orderID}/payments/${id}`);
    },
    onSuccess: () => {
      invalidate();
      notifications.show({ message: "Платёж удалён", color: "teal" });
    },
    onError: () => notifications.show({ message: "Не удалось удалить платёж", color: "red" }),
  });

  const payments = paymentsQ.data?.items ?? [];
  const incoming = payments.filter((p) => p.direction === "incoming");
  const outgoing = payments.filter((p) => p.direction === "outgoing");

  return (
    <Stack gap="lg">
      {summaryQ.data && <SummaryPanel summary={summaryQ.data} />}

      <PaymentSection
        title="От заказчика (входящие)"
        direction="incoming"
        contragentName={order.client_name ?? "(заказчик не выбран)"}
        contragentID={order.client_id}
        companyID={order.company_id}
        payments={incoming}
        isLocked={isLocked}
        onCreate={(body) => createMut.mutate(body)}
        onUpdate={(id, body) => updateMut.mutate({ id, body })}
        onDelete={(id) => deleteMut.mutate(id)}
        creating={createMut.isPending}
      />

      <PaymentSection
        title="Перевозчику (исходящие)"
        direction="outgoing"
        contragentName={order.carrier_name ?? "(перевозчик не выбран)"}
        contragentID={order.carrier_id}
        companyID={order.company_id}
        payments={outgoing}
        isLocked={isLocked}
        onCreate={(body) => createMut.mutate(body)}
        onUpdate={(id, body) => updateMut.mutate({ id, body })}
        onDelete={(id) => deleteMut.mutate(id)}
        creating={createMut.isPending}
      />
    </Stack>
  );
}

// ─── Сводная плашка сверху ──────────────────────────────────────────────

function SummaryPanel({ summary }: { summary: PaymentSummary }) {
  const clientDebtColor =
    summary.client_debt > 0 ? "red" : summary.client_debt < 0 ? "orange" : "green";
  const carrierDebtColor =
    summary.carrier_debt > 0 ? "red" : summary.carrier_debt < 0 ? "orange" : "green";

  return (
    <Paper p="md" withBorder>
      <Group grow align="flex-start">
        <Stack gap={4}>
          <Text size="xs" c="dimmed">Заказчик должен нам</Text>
          <Group gap="xs">
            <Text ff="monospace" size="sm">
              {fmt(summary.client_paid)} / {fmt(summary.client_total)}
            </Text>
            <Badge color={clientDebtColor} variant="light" radius="sm">
              долг {fmt(summary.client_debt)}
            </Badge>
          </Group>
        </Stack>
        <Stack gap={4}>
          <Text size="xs" c="dimmed">Мы должны перевозчику</Text>
          <Group gap="xs">
            <Text ff="monospace" size="sm">
              {fmt(summary.carrier_paid)} / {fmt(summary.carrier_total)}
            </Text>
            <Badge color={carrierDebtColor} variant="light" radius="sm">
              долг {fmt(summary.carrier_debt)}
            </Badge>
          </Group>
        </Stack>
        <Stack gap={4}>
          <Text size="xs" c="dimmed">Комиссия (факт / ожидание)</Text>
          <Group gap="xs">
            <Text ff="monospace" size="sm" fw={600}>
              {fmt(summary.commission)} / {fmt(summary.commission_expected)}
            </Text>
          </Group>
        </Stack>
      </Group>
    </Paper>
  );
}

// ─── Секция «От заказчика» или «Перевозчику» ────────────────────────────

interface SectionProps {
  title: string;
  direction: PaymentDirection;
  contragentName: string;
  contragentID?: string;
  companyID: string;
  payments: Payment[];
  isLocked: boolean;
  onCreate: (body: PaymentCreateRequest) => void;
  onUpdate: (id: string, body: PaymentCreateRequest) => void;
  onDelete: (id: string) => void;
  creating: boolean;
}

function PaymentSection({
  title,
  direction,
  contragentName,
  contragentID,
  companyID,
  payments,
  isLocked,
  onCreate,
  onUpdate,
  onDelete,
  creating,
}: SectionProps) {
  const [adding, setAdding] = useState(false);
  const [editingID, setEditingID] = useState<string | null>(null);
  const total = payments.reduce((a, p) => a + p.amount, 0);

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="sm">
        <div>
          <Title order={5}>{title}</Title>
          <Text size="xs" c="dimmed">{contragentName}</Text>
        </div>
        {!isLocked && !adding && (
          <Button
            size="xs"
            leftSection={<IconPlus size={14} />}
            variant="light"
            onClick={() => { setAdding(true); setEditingID(null); }}
          >
            Добавить платёж
          </Button>
        )}
      </Group>

      {adding && (
        <PaymentForm
          direction={direction}
          contragentID={contragentID}
          companyID={companyID}
          submitting={creating}
          onSubmit={(body) => { onCreate(body); setAdding(false); }}
          onCancel={() => setAdding(false)}
        />
      )}

      {payments.length === 0 && !adding && (
        <Text c="dimmed" size="sm">Платежей нет</Text>
      )}

      {payments.length > 0 && (
        <Table striped highlightOnHover withTableBorder withColumnBorders mt="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: 100 }}>Дата</Table.Th>
              <Table.Th style={{ width: 100 }}>№ п/п</Table.Th>
              <Table.Th style={{ width: 130 }} ta="right">Сумма</Table.Th>
              <Table.Th>Примечание</Table.Th>
              <Table.Th style={{ width: 90 }}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {payments.map((p) => editingID === p.id ? (
              <Table.Tr key={p.id}>
                <Table.Td colSpan={5} p={0}>
                  <PaymentForm
                    direction={direction}
                    contragentID={contragentID}
                    companyID={companyID}
                    initial={p}
                    submitting={false}
                    onSubmit={(body) => { onUpdate(p.id, body); setEditingID(null); }}
                    onCancel={() => setEditingID(null)}
                  />
                </Table.Td>
              </Table.Tr>
            ) : (
              <Table.Tr key={p.id}>
                <Table.Td>{p.payment_date.slice(0, 10).split("-").reverse().join(".")}</Table.Td>
                <Table.Td>{p.payment_number || "—"}</Table.Td>
                <Table.Td ta="right" ff="monospace">{fmt(p.amount)}</Table.Td>
                <Table.Td>{p.note || "—"}</Table.Td>
                <Table.Td>
                  {!isLocked && (
                    <Group gap={4} wrap="nowrap">
                      <Tooltip label="Редактировать">
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => { setEditingID(p.id); setAdding(false); }}
                        >
                          <IconPencil size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Удалить">
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => {
                            if (window.confirm(`Удалить платёж на ${fmt(p.amount)} от ${p.payment_date.slice(0, 10)}?`)) {
                              onDelete(p.id);
                            }
                          }}
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
            <Table.Tr>
              <Table.Td colSpan={2}><Text fw={600} size="sm">Итого</Text></Table.Td>
              <Table.Td ta="right" ff="monospace" fw={600}>{fmt(total)}</Table.Td>
              <Table.Td colSpan={2}></Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}

// ─── Форма ввода одного платежа ─────────────────────────────────────────

interface FormProps {
  direction: PaymentDirection;
  contragentID?: string;
  companyID: string;
  initial?: Payment;
  submitting: boolean;
  onSubmit: (body: PaymentCreateRequest) => void;
  onCancel: () => void;
}

function PaymentForm({
  direction,
  contragentID,
  companyID,
  initial,
  submitting,
  onSubmit,
  onCancel,
}: FormProps) {
  const form = useForm<{
    payment_date: Date | null;
    payment_number: string;
    amount: number | "";
    currency: string;
    note: string;
  }>({
    initialValues: {
      payment_date: initial ? new Date(initial.payment_date) : new Date(),
      payment_number: initial?.payment_number ?? "",
      amount: initial?.amount ?? "",
      currency: initial?.currency ?? "RUR",
      note: initial?.note ?? "",
    },
    validate: {
      payment_date: (v) => (v ? null : "Обязательно"),
      amount: (v) => (v !== "" && Number(v) > 0 ? null : "Сумма > 0"),
    },
  });

  function handleSubmit() {
    const result = form.validate();
    if (result.hasErrors) return;
    const v = form.values;
    const body: PaymentCreateRequest = {
      direction,
      company_id: companyID,
      payment_number: v.payment_number,
      payment_date: toDateISO(v.payment_date),
      amount: Number(v.amount),
      currency: v.currency,
      note: v.note,
    };
    if (direction === "incoming" && contragentID) {
      body.client_id = contragentID;
      body.contragent_kind = "client";
    }
    if (direction === "outgoing" && contragentID) {
      body.carrier_id = contragentID;
      body.contragent_kind = "carrier";
    }
    onSubmit(body);
  }

  return (
    <Paper p="sm" bg="var(--mantine-color-gray-0)">
      <Stack gap="xs">
        <Group grow>
          <SafeDateInput
            label="Дата"
            value={form.values.payment_date}
            onChange={(d) => form.setFieldValue("payment_date", d)}
            error={form.errors.payment_date}
          />
          <TextInput
            label="№ п/п"
            placeholder="номер платёжки"
            {...form.getInputProps("payment_number")}
          />
          <NumberInput
            label="Сумма"
            hideControls
            thousandSeparator=" "
            decimalScale={2}
            value={form.values.amount}
            onChange={(v) => form.setFieldValue("amount", v === "" ? "" : Number(v))}
            error={form.errors.amount}
          />
          <Select
            label="Валюта"
            data={["RUR", "USD", "EUR", "BYN", "KZT"]}
            {...form.getInputProps("currency")}
            style={{ maxWidth: 110 }}
          />
        </Group>
        <Textarea
          label="Примечание"
          placeholder="Например: авансовый платёж"
          autosize
          minRows={1}
          {...form.getInputProps("note")}
        />
        <Group justify="flex-end" gap="xs">
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconX size={14} />}
            onClick={onCancel}
          >
            Отмена
          </Button>
          <Button
            size="xs"
            leftSection={<IconCheck size={14} />}
            loading={submitting}
            onClick={handleSubmit}
          >
            Сохранить
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}
